/*
 * Media Planner draft identity: one logical plan per saved draft.
 *
 * The planner keeps a browser copy of each plan (a local planner entry) next
 * to the draft saved on the server. Opening the planner, Load Draft, a
 * refresh or Back must reopen the SAME local entry for the same saved draft:
 * a fresh local id per visit made every visit list the saved plan again, as
 * an identical-looking draft stamped with the time it was opened
 * (iScreeningRoom, Oct 20 2026 00:21–00:24 UTC, Oct 19 5:21–5:24 PM Pacific:
 * five visits, five "MovieGoer Audience Growth" entries, no save at all).
 *
 * Pure helpers only. `readBinding(localId)` is supplied by the page and
 * returns what the stored local copy says about itself, or null:
 *   { serverDraftId, unsavedChanges, serverSavedAt, supersededServerDraftId }
 * where unsavedChanges is true, false, or null when the copy never recorded
 * it (a copy the previous planner wrote).
 * Byte-identical in Cortex and Quantum.
 */

// A saved draft that has no stored local id opens into this derived id, so
// every visit to that draft reopens the same entry.
export const SERVER_DRAFT_LOCAL_ID_PREFIX = "smp_srv_";

const MAX_LOCAL_PLANNER_ID_LENGTH = 160;

export const normalizeServerDraftId = (value) => {
  const text = String(value ?? "").trim();
  if (!/^\d+$/.test(text)) return "";
  const id = Number(text);
  return Number.isSafeInteger(id) && id > 0 ? String(id) : "";
};

export const serverDraftIdOf = (draft) =>
  normalizeServerDraftId(draft?.serverDraftId ?? draft?.id ?? draft?.campaignId);

/** Whether two advertiser ids name the same advertiser ("33" and 33 do). */
export const sameAdvertiserId = (a, b) => {
  const left = String(a ?? "").trim();
  const right = String(b ?? "").trim();
  if (!left || !right) return false;
  if (/^\d+$/.test(left) && /^\d+$/.test(right)) return Number(left) === Number(right);
  return left === right;
};

export const localPlannerIdForServerDraft = (serverDraftId) => {
  const id = normalizeServerDraftId(serverDraftId);
  return id ? `${SERVER_DRAFT_LOCAL_ID_PREFIX}${id}` : "";
};

const usableLocalPlannerId = (value) => {
  const text = String(value ?? "").trim();
  return text && text.length <= MAX_LOCAL_PLANNER_ID_LENGTH ? text : "";
};

const entriesForAdvertiser = (plannerIndex, advertiserKey) =>
  (Array.isArray(plannerIndex) ? plannerIndex : []).filter(
    (entry) => entry?.id && (!advertiserKey || entry.advertiserKey === advertiserKey)
  );

const readBindingSafely = (readBinding, localId) => {
  try {
    return readBinding?.(localId) || null;
  } catch {
    return null;
  }
};

const boundServerDraftId = (readBinding, localId) =>
  normalizeServerDraftId(readBindingSafely(readBinding, localId)?.serverDraftId);

const timeOf = (value) => {
  const ms = Date.parse(String(value || ""));
  return Number.isFinite(ms) ? ms : 0;
};

/**
 * The local planner entry a saved server draft opens into. In order: the
 * remembered entry if it already holds this draft, the draft's own stored
 * local id if this browser holds it, the most recent local copy of this
 * draft, the draft's stored local id (first visit on this browser), then the
 * id derived from the draft id. Never a fresh random id.
 */
export const resolveLocalPlannerIdForServerDraft = ({
  draft,
  plannerIndex = [],
  readBinding,
  advertiserKey = "",
  preferredLocalId = "",
} = {}) => {
  const serverId = serverDraftIdOf(draft);
  const storedLocalId = usableLocalPlannerId(draft?.localDraftId);
  if (!serverId) return storedLocalId;

  const entries = entriesForAdvertiser(plannerIndex, advertiserKey);
  const copies = entries.filter((entry) => boundServerDraftId(readBinding, entry.id) === serverId);
  const preferred = usableLocalPlannerId(preferredLocalId);
  if (preferred && copies.some((entry) => entry.id === preferred)) return preferred;
  if (storedLocalId && copies.some((entry) => entry.id === storedLocalId)) return storedLocalId;
  if (copies.length) {
    return [...copies].sort((a, b) => timeOf(b.updatedAt) - timeOf(a.updatedAt))[0].id;
  }
  if (storedLocalId) {
    // Adopt the plan's own id unless this browser uses it for another draft.
    const owner = boundServerDraftId(readBinding, storedLocalId);
    if (!owner || owner === serverId) return storedLocalId;
  }
  return localPlannerIdForServerDraft(serverId);
};

const draftTitle = (draft) =>
  String(draft?.campaignName || draft?.formData?.campaignName || "").trim() || "Untitled plan";

// A local entry's title is "<advertiser> — <campaign>"; the list is already
// one advertiser's, so only the campaign part is shown.
const localEntryTitle = (entry) => {
  const title = String(entry?.title || "").trim();
  const advertiser = String(entry?.advertiserName || "").trim();
  if (!title || (advertiser && title === advertiser)) return "Untitled plan";
  const prefix = advertiser ? `${advertiser} — ` : "";
  const name = prefix && title.startsWith(prefix) ? title.slice(prefix.length).trim() : title;
  return name || "Untitled plan";
};

/**
 * Each logical plan once: every saved draft (server order, newest first), then
 * browser-only plans that hold work and were never saved. A local copy of a
 * saved draft is that draft, never a second option; an empty local planner
 * offers nothing to resume.
 */
export const buildPlannerDraftOptions = ({
  serverDrafts = [],
  plannerIndex = [],
  readBinding,
  advertiserKey = "",
  hasLocalContent = () => true,
} = {}) => {
  const options = [];
  const listed = new Set();
  const storedLocalIds = new Set();
  for (const draft of Array.isArray(serverDrafts) ? serverDrafts : []) {
    const serverDraftId = serverDraftIdOf(draft);
    if (!serverDraftId || listed.has(serverDraftId)) continue;
    listed.add(serverDraftId);
    const storedLocalId = usableLocalPlannerId(draft?.localDraftId);
    if (storedLocalId) storedLocalIds.add(storedLocalId);
    options.push({
      key: `server:${serverDraftId}`,
      kind: "server",
      serverDraftId,
      draft,
      title: draftTitle(draft),
      savedAt: draft?.savedAt || draft?.updatedAt || null,
      flightStart: draft?.formData?.flightStart || null,
      flightEnd: draft?.formData?.flightEnd || null,
    });
  }

  const localOnly = entriesForAdvertiser(plannerIndex, advertiserKey)
    .filter((entry) => !boundServerDraftId(readBinding, entry.id))
    .filter((entry) => !storedLocalIds.has(entry.id))
    .filter((entry) => {
      try {
        return Boolean(hasLocalContent(entry.id));
      } catch {
        return false;
      }
    })
    .sort((a, b) => timeOf(b.updatedAt) - timeOf(a.updatedAt));
  for (const entry of localOnly) {
    options.push({
      key: `local:${entry.id}`,
      kind: "local",
      localPlannerId: entry.id,
      title: localEntryTitle(entry),
      savedAt: null,
      // Set when these are a saved plan's changes this browser kept apart.
      supersededServerDraftId: normalizeServerDraftId(
        readBindingSafely(readBinding, entry.id)?.supersededServerDraftId
      ),
    });
  }
  return options;
};

/**
 * Browser copies of listed saved drafts that never recorded whether they hold
 * unsaved edits (the previous planner did not). Each must be compared with
 * its saved draft before the saved version may be written over it.
 */
export const unmarkedCopiesOfListedDrafts = ({
  serverDrafts = [],
  plannerIndex = [],
  readBinding,
  advertiserKey = "",
} = {}) => {
  const drafts = new Map();
  for (const draft of Array.isArray(serverDrafts) ? serverDrafts : []) {
    const id = serverDraftIdOf(draft);
    if (id && !drafts.has(id)) drafts.set(id, draft);
  }
  const copies = [];
  for (const entry of entriesForAdvertiser(plannerIndex, advertiserKey)) {
    const binding = readBindingSafely(readBinding, entry.id);
    if (!binding || binding.unsavedChanges !== null) continue;
    const draft = drafts.get(normalizeServerDraftId(binding.serverDraftId));
    if (draft) copies.push({ localPlannerId: entry.id, draft });
  }
  return copies;
};

/** The listed drafts with this saved draft in its place, newest first. */
export const upsertServerDraft = (drafts, draft) => {
  const list = Array.isArray(drafts) ? drafts : [];
  const id = serverDraftIdOf(draft);
  if (!id) return list;
  return [draft, ...list.filter((entry) => serverDraftIdOf(entry) !== id)];
};

/** When a draft was saved, short: "Oct 19, 11:05 AM" (the year only when it is not this year's). */
export const formatPlannerSavedTime = (iso, { now = new Date() } = {}) => {
  const date = new Date(String(iso || ""));
  if (!iso || Number.isNaN(date.getTime())) return "";
  const options = { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" };
  if (date.getFullYear() !== now.getFullYear()) options.year = "numeric";
  try {
    return date.toLocaleString(undefined, options);
  } catch {
    return "";
  }
};

/**
 * One line per plan, readable on a phone: what tells same-named plans apart
 * comes first, because a narrow picker cuts the end off. A saved plan shows
 * its id and when it was SAVED (the record's own time, never the time it was
 * opened); a browser-only plan says it is not saved.
 */
export const formatPlannerDraftOptionLabel = (option, formatTimestamp = formatPlannerSavedTime) => {
  if (!option) return "";
  if (option.kind === "local") {
    return option.supersededServerDraftId
      ? `Unsaved changes to #${option.supersededServerDraftId} · ${option.title}`
      : `Not saved · ${option.title}`;
  }
  let saved = "";
  try {
    saved = option.savedAt ? String(formatTimestamp(option.savedAt) || "") : "";
  } catch {
    saved = "";
  }
  return [`#${option.serverDraftId}`, saved, option.title].filter(Boolean).join(" · ");
};

export const plannerDraftOptionKey = ({ activePlannerId = "", serverDraftId = null, readBinding } = {}) => {
  const serverId =
    normalizeServerDraftId(serverDraftId) || boundServerDraftId(readBinding, activePlannerId);
  if (serverId) return `server:${serverId}`;
  const localId = usableLocalPlannerId(activePlannerId);
  return localId ? `local:${localId}` : "";
};

/**
 * The verified account a plan choice is remembered for: the signed-in user's
 * id as the server's auth check returned it. Without one, no choice is
 * remembered or used.
 */
export const plannerAccountKey = (userId) => {
  const text = String(userId ?? "").trim();
  return /^[A-Za-z0-9_-]{1,64}$/.test(text) ? `acct_${text}` : "";
};

/**
 * The plan the operator chose, as stored: a saved draft's id, or the local id
 * of a plan with no saved draft yet. Null when there is nothing usable.
 */
export const parsePlannerChoice = (stored) => {
  let value = stored;
  if (typeof stored === "string") {
    try {
      value = JSON.parse(stored);
    } catch {
      return null;
    }
  }
  if (!value || typeof value !== "object") return null;
  const serverDraftId = normalizeServerDraftId(value.serverDraftId);
  const localPlannerId = serverDraftId ? "" : usableLocalPlannerId(value.localPlannerId);
  return serverDraftId || localPlannerId ? { serverDraftId, localPlannerId } : null;
};

/** The choice to store when the operator picks a listed plan. */
export const plannerChoiceOfOption = (option) => {
  if (option?.kind === "server") return { serverDraftId: option.serverDraftId, localPlannerId: "" };
  if (option?.kind === "local") return { serverDraftId: "", localPlannerId: option.localPlannerId };
  return null;
};

/** What is stored for a choice, or "" when there is nothing to store. */
export const serializePlannerChoice = (choice, now = new Date()) => {
  const parsed = parsePlannerChoice(choice);
  return parsed ? JSON.stringify({ ...parsed, chosenAt: now.toISOString() }) : "";
};

// The listed plan a stored choice names: its saved draft (once a chosen
// browser-only plan is saved, the draft it became), else the browser-only plan.
const optionForChoice = (list, choice, readBinding) => {
  const serverId = choice.serverDraftId || boundServerDraftId(readBinding, choice.localPlannerId);
  if (serverId) return list.find((entry) => entry.kind === "server" && entry.serverDraftId === serverId) || null;
  return (
    list.find(
      (entry) =>
        (entry.kind === "local" && entry.localPlannerId === choice.localPlannerId) ||
        (entry.kind === "server" && usableLocalPlannerId(entry.draft?.localDraftId) === choice.localPlannerId)
    ) || null
  );
};

/**
 * Which plan entering the planner (or Load Draft) opens:
 * - an explicit draft id from the route wins, if this advertiser has it;
 * - else the plan the operator last chose for this advertiser on this
 *   verified account, while it is still listed;
 * - else the only plan there is;
 * - else nothing is guessed: several plans → "select", none → "new".
 * Only plans the operator chose count: one that opened by itself is not a
 * choice. The options are the plans the server listed for this caller.
 */
export const choosePlannerResume = ({
  options = [],
  explicitServerDraftId = "",
  choice = null,
  readBinding,
} = {}) => {
  const list = Array.isArray(options) ? options : [];
  const explicitId = normalizeServerDraftId(explicitServerDraftId);
  if (explicitId) {
    const option = list.find((entry) => entry.kind === "server" && entry.serverDraftId === explicitId);
    return option ? { action: "resume", option, reason: "explicit" } : { action: "explicit_not_found", option: null };
  }
  const chosen = parsePlannerChoice(choice);
  if (chosen) {
    const option = optionForChoice(list, chosen, readBinding);
    if (option) return { action: "resume", option, reason: "chosen" };
  }
  if (list.length === 1) return { action: "resume", option: list[0], reason: "only" };
  if (list.length === 0) return { action: "new", option: null };
  return { action: "select", option: null };
};

/**
 * Whether reopening a saved draft keeps this browser's copy instead of the
 * server version: only when the copy holds edits that were never confirmed
 * saved AND the saved draft has not changed since that copy was based on it.
 */
export const shouldKeepUnsavedLocalCopy = ({ binding, draft } = {}) => {
  if (!binding?.unsavedChanges) return false;
  const serverSavedAt = draft?.savedAt || draft?.updatedAt || null;
  if (!serverSavedAt) return true;
  return Boolean(binding.serverSavedAt) && String(binding.serverSavedAt) === String(serverSavedAt);
};
